import { marked } from 'marked'
import { FC, FormEvent, useEffect, useRef, useState } from 'react'
import { FaPiggyBank } from 'react-icons/fa'
import { MdCheckCircle, MdPerson } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Headline from '../../../Elements/Headline'
import LoadingSpinner from '../../../Elements/LoadingSpinner'
import Text from '../../../Elements/Text'
import Button from '../../../Formik/Button'
import { Language } from '../../../../lib/Enums/Language'
import { useAuthenticatedUser, useSaveUser } from '../../../../lib/Hooks/User'
import { ChatMessage, ChatUsage } from '../../lib/API/AIAPI'
import { useCompleteChat, useSendChat } from '../../lib/Hooks/AI'

// A plain greeting in the app's current language, in that language — this is
// the hidden seed message that kicks off the AI budget conversation (see
// where it's built below for why).
const SEED_GREETING: Record<Language, string> = {
  [Language.DANISH]: 'Hej',
  [Language.GERMAN]: 'Guten Tag',
  [Language.ENGLISH]: 'Hi',
}

// The assistant replies in Markdown; render it to HTML once a message has
// finished streaming. `breaks` keeps single newlines as line breaks.
const renderMarkdown = (content: string): string =>
  marked.parse(content, { gfm: true, breaks: true }) as string

const pillClass =
  'inline-flex items-center rounded-full border border-theme-blue px-3 py-1 text-sm transition'

// Marks where control markup (radio/checkbox options) begins in a streamed
// response. Plain markdown lists are NOT control markup — they render as lists.
const CONTROL_MARKUP = /<input|<details|<\/?(?:code|pre|label|summary)\b|```/i

// The bouncing "Elisabeth is thinking" dots.
const ThinkingDots = () => (
  <span className='inline-flex gap-1'>
    <span className='h-2 w-2 animate-bounce rounded-full bg-theme-blue [animation-delay:-0.3s]' />
    <span className='h-2 w-2 animate-bounce rounded-full bg-theme-blue [animation-delay:-0.15s]' />
    <span className='h-2 w-2 animate-bounce rounded-full bg-theme-blue' />
  </span>
)

// While a message is still streaming, hide any control/list markup (and
// everything after it) so the user only sees the readable question text. The
// controls are rendered as pills once the message has finished streaming.
const streamingDisplay = (raw: string): string => {
  // Hide the completion marker and the budget JSON that follows it.
  const finishedAt = raw.indexOf('[FINISHED]')
  const base = finishedAt === -1 ? raw : raw.slice(0, finishedAt)
  const controlStart = base.search(CONTROL_MARKUP)
  let text = controlStart === -1 ? base : base.slice(0, controlStart)
  // Drop any complete inline tags and a trailing partial tag mid-stream.
  text = text.replace(/<[^>]*>/g, '')
  const partialTag = text.lastIndexOf('<')
  if (partialTag !== -1) text = text.slice(0, partialTag)
  return text.replace(/\n\s*$/, '')
}

// The human-readable answer for a form control: its wrapping/associated label
// text, otherwise its value attribute.
const controlLabel = (input: HTMLInputElement, doc: Document): string => {
  const wrapping = input.closest('label')
  if (wrapping?.textContent?.trim()) return wrapping.textContent.trim()

  if (input.id) {
    const associated = doc.querySelector(`label[for="${input.id}"]`)
    if (associated?.textContent?.trim()) return associated.textContent.trim()
  }

  const sibling = input.nextSibling?.textContent?.trim()
  if (sibling) return sibling

  return input.getAttribute('value')?.trim() ?? ''
}

// Is `value` already present in the (comma-separated) input text?
const isValueInInput = (input: string, value: string): boolean =>
  input.toLowerCase().includes(value.toLowerCase())

// Add or remove `value` from the input text, keeping it comma-separated and
// capitalised so the composed answer reads naturally.
const toggleInputValue = (input: string, value: string): string => {
  const at = input.toLowerCase().indexOf(value.toLowerCase())

  let result =
    at >= 0
      ? input.slice(0, at) + input.slice(at + value.length)
      : input.trim()
        ? `${input.trim()}, ${value}`
        : value

  // Tidy up separators left behind by add/remove.
  result = result
    .replace(/\s*,\s*,\s*/g, ', ')
    .replace(/^[\s,]+/, '')
    .replace(/[\s,]+$/, '')
    .replace(/\s{2,}/g, ' ')

  return result ? result.charAt(0).toUpperCase() + result.slice(1) : ''
}

// Multi-select checkbox options. Clicking a pill toggles its value in the chat
// input; the answer is submitted from the main chat input's "Send svar".
const CheckboxOptions: FC<{
  options: string[]
  input: string
  onToggle: (value: string) => void
}> = ({ options, input, onToggle }) => {
  return (
    <div className='mt-2 flex flex-wrap gap-2'>
      {options.map((option, i) => {
        const active = isValueInInput(input, option)
        return (
          <button
            key={i}
            type='button'
            onClick={() => onToggle(option)}
            className={`${pillClass} ${
              active
                ? 'bg-theme-blue text-white'
                : 'bg-white text-theme-blue hover:bg-theme-blue hover:text-white'
            }`}
          >
            {option}
          </button>
        )
      })}
    </div>
  )
}

const LOADING_MESSAGE_INTERVAL = 20000
const FADE_DURATION = 500

const AIGuidePaywall = () => {
  const { t, i18n } = useTranslation(['budget'])
  // Reassuring messages cycled while the budget is being built (20s each).
  const LOADING_MESSAGES = [
    t('budget:aiGuidePaywall.loadingMessage1'),
    t('budget:aiGuidePaywall.loadingMessage2'),
    t('budget:aiGuidePaywall.loadingMessage3'),
  ]
  // Hidden greeting used to kick off the conversation so the agent asks the
  // first question itself. It's sent to the API but never shown in the chat.
  // Which language the agent replies in is decided by which DO agent the
  // backend routes this request to (based on the user's language), not by
  // anything in this message — this is just a plain, natural first turn.
  const seedMessage: ChatMessage = {
    role: 'user',
    content: SEED_GREETING[i18n.language as Language] ?? SEED_GREETING[Language.DANISH],
  }
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  // Index of the message currently being typed/streamed, or null when idle.
  // A streaming message shows raw text; a finished one renders as Markdown.
  const [streamingIndex, setStreamingIndex] = useState<number | null>(null)
  // Set once the agent signals completion — fades out the chat and shows the
  // "preparing your budget" spinner.
  const [isCompleting, setIsCompleting] = useState(false)
  // The generated budget waiting for the user to confirm payment. Set once the
  // agent is done ([FINISHED]); until it's created it drives the confirm page.
  const [pendingCompletion, setPendingCompletion] = useState<{
    budget: unknown
    usage?: ChatUsage
    messages: ChatMessage[]
  } | null>(null)
  // Whether the user has ticked the "accept conditions" checkbox.
  const [conditionsAccepted, setConditionsAccepted] = useState(false)
  // Phone number captured on the confirm page when the user doesn't have one.
  const [phone, setPhone] = useState('')
  // Set once the user confirms — the budget is being created via /complete/.
  const [isCreating, setIsCreating] = useState(false)
  // Set once the budget has been built — swaps the spinner for a checkmark.
  const [isReady, setIsReady] = useState(false)
  // Which reassuring loading message is showing, and its fade state.
  const [loadingStep, setLoadingStep] = useState(0)
  const [lineVisible, setLineVisible] = useState(true)
  const navigate = useNavigate()
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const startedRef = useRef(false)
  const { mutateAsync: sendChat } = useSendChat()
  const { mutateAsync: completeChat } = useCompleteChat()
  const { data: user } = useAuthenticatedUser()
  const { mutateAsync: saveUser } = useSaveUser()

  // Whether we already have a phone number on file — if not, the confirm page
  // asks for one (required) and PATCHes it to /user/ before creating the budget.
  const hasPhone = Boolean(user?.phone?.trim())

  // We're "busy" only while actively streaming a reply — tracked precisely by
  // streamingIndex, not the mutation's isPending (which can linger true).
  const isStreaming = streamingIndex !== null

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    })
    // Also re-scroll once streaming ends (streamingIndex -> null): that's
    // when the radio/checkbox pills first render (hidden while streaming),
    // which can grow the bubble taller than the last messages-driven scroll
    // accounted for, e.g. once pills wrap onto multiple lines.
  }, [messages, streamingIndex])

  // While the budget builds, cycle the reassuring loading messages: hold for
  // 20s, fade the current line out, swap it, and fade the next one in.
  useEffect(() => {
    if (!isCreating || isReady) return
    if (loadingStep >= LOADING_MESSAGES.length - 1) return

    let swapTimer: ReturnType<typeof setTimeout>
    const holdTimer = setTimeout(() => {
      setLineVisible(false)
      swapTimer = setTimeout(() => {
        setLoadingStep((step) => step + 1)
        setLineVisible(true)
      }, FADE_DURATION)
    }, LOADING_MESSAGE_INTERVAL)

    return () => {
      clearTimeout(holdTimer)
      clearTimeout(swapTimer)
    }
  }, [isCompleting, isReady, loadingStep])

  // Append an empty assistant bubble and stream the agent's reply into it.
  // `display` is what the chat shows; `apiMessages` is the full history sent to
  // the API (which includes the hidden seed greeting).
  const streamAgentReply = async (
    display: ChatMessage[],
    apiMessages: ChatMessage[],
  ) => {
    const assistantIndex = display.length
    setMessages([...display, { role: 'assistant', content: '' }])
    setStreamingIndex(assistantIndex)

    // Track the running reply locally: the completion marker can arrive split
    // across tokens, so we test the accumulated string (reading it back out of
    // state inside the closure would be racy).
    let accumulated = ''

    try {
      const { done, budget, usage } = await sendChat({
        messages: apiMessages,
        language: user?.language_interface ?? Language.ENGLISH,
        onToken: (token) => {
          accumulated += token
          setMessages((prev) => {
            const updated = [...prev]
            updated[assistantIndex] = {
              role: 'assistant',
              content: updated[assistantIndex].content + token,
            }
            return updated
          })
          // As soon as the completion marker streams in, drop the chat and show
          // the spinner — the budget JSON keeps streaming behind the fade, so
          // the empty "[FINISHED]…" bubble never renders.
          if (accumulated.includes('[FINISHED]')) setIsCompleting(true)
        },
      })

      // Agent is done → drop the marker bubble and show the payment
      // confirmation. The budget is only created once the user confirms.
      if (done) {
        setMessages(display)
        setIsCompleting(true)
        setPendingCompletion({ budget, usage, messages: apiMessages })
      }
    } finally {
      setStreamingIndex(null)
    }
  }

  // User accepted the conditions and confirmed → create the budget via
  // /complete/, then swap the spinner for the checkmark and redirect to it.
  const confirmAndCreate = async () => {
    if (!pendingCompletion || !conditionsAccepted || isCreating) return
    // A phone number is required — either already on file or entered here.
    if (!hasPhone && !phone.trim()) return
    setIsCreating(true)
    try {
      // Save the phone number to the user before creating the budget.
      if (!hasPhone && user) {
        await saveUser({ ...user, phone: phone.trim() })
      }
      const budget = await completeChat(pendingCompletion)
      setIsReady(true)
      setTimeout(() => navigate(`/budget/${budget.id}/`), 1000)
    } catch {
      // Let the user try again if saving the number or creating the budget failed.
      setIsCreating(false)
    }
  }

  // On mount, kick off the conversation with a hidden greeting so the agent
  // asks the first question itself. The thinking bubble shows until it streams in.
  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    streamAgentReply([], [seedMessage])
  }, [])

  const submitMessage = async (raw: string) => {
    const content = raw.trim()
    if (!content || isStreaming || isCompleting) return

    const display: ChatMessage[] = [...messages, { role: 'user', content }]
    setInput('')
    // Keep focus on the input so the user can keep typing without re-clicking.
    inputRef.current?.focus()

    await streamAgentReply(display, [seedMessage, ...display])
  }

  const sendMessage = (e: FormEvent) => {
    e.preventDefault()
    submitMessage(input)
  }

  // Checkbox pill → toggle its value in the chat input.
  const toggleOption = (value: string) => {
    setInput((prev) => toggleInputValue(prev, value))
    inputRef.current?.focus()
  }

  // Render finished Markdown. Elisabeth's suggested answers arrive as form
  // controls (radio → pick one, instant reply; checkbox → pick many, submit)
  // or, as a fallback, a list. These are pulled out and rendered as controls,
  // and stripped from the text so they don't also show up as raw markup.
  const renderAssistantMarkdown = (content: string, showControls: boolean) => {
    const doc = new DOMParser().parseFromString(
      renderMarkdown(content),
      'text/html',
    )

    // The agent sometimes wraps the controls in a <code>/<pre> block, which
    // Markdown renders as escaped text — so the <input>s never become real
    // elements. Detect such blocks and replace them with their parsed HTML so
    // the controls become part of the DOM (works inside <details> too).
    doc.body.querySelectorAll('code, pre').forEach((block) => {
      const source = block.textContent ?? ''
      const start = source.search(/<(input|details)/i)
      if (start === -1) return
      // Slice from the first control tag so a leading language hint (e.g. the
      // "html" from an inline ```html fence) isn't kept as stray text.
      const markup = source.slice(start)
      const parsed = new DOMParser().parseFromString(markup, 'text/html').body
      const target = block.closest('pre') ?? block
      target.replaceWith(...Array.from(parsed.childNodes))
    })

    const collect = (selector: string): string[] => {
      const values: string[] = []
      const seen = new Set<string>()
      doc.body.querySelectorAll(selector).forEach((el) => {
        const text =
          el.tagName === 'INPUT'
            ? controlLabel(el as HTMLInputElement, doc)
            : (el.textContent?.trim() ?? '')
        if (text && !seen.has(text)) {
          seen.add(text)
          values.push(text)
        }
      })
      return values
    }

    // Only radio/checkbox inputs become instant-reply pills. Plain markdown
    // lists are left to render as normal lists.
    const radios = collect('input[type="radio"]')
    const checkboxes = collect('input[type="checkbox"]')
    const instantOptions = radios

    // Strip only the controls (and the label/list-item/details wrapper each one
    // lived in) so they don't also render as text. Lists that held no input are
    // left intact. For a bare input the label is an adjacent text node.
    doc.body.querySelectorAll('input').forEach((input) => {
      const wrapper =
        input.closest('li') ??
        input.closest('label') ??
        input.closest('details')
      if (wrapper) {
        wrapper.remove()
        return
      }
      const next = input.nextSibling
      if (next && next.nodeType === Node.TEXT_NODE) next.remove()
      input.remove()
    })

    // The agent sometimes writes the option's label as a separate <label
    // for="id"> element that's a SIBLING of its <input> rather than a
    // wrapping ancestor or an adjacent text node — the removal above only
    // handles those two cases, so a sibling <label> (and its <br>) survives
    // as duplicate plain text under the pills. Remove any leftover text node
    // that's an exact match for a collected option label.
    const optionLabels = new Set(
      [...radios, ...checkboxes].map((label) => label.toLowerCase()),
    )
    if (optionLabels.size > 0) {
      const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
      const staleTextNodes: ChildNode[] = []
      let node: Node | null
      while ((node = walker.nextNode())) {
        const text = node.textContent?.trim().toLowerCase()
        if (text && optionLabels.has(text)) staleTextNodes.push(node as ChildNode)
      }
      staleTextNodes.forEach((textNode) => textNode.remove())
    }

    // Drop now-empty list/details wrappers left behind after removing controls,
    // but keep lists that still have text (plain markdown lists).
    doc.body
      .querySelectorAll('ul, ol, details, summary')
      .forEach((el) => {
        if (!el.textContent?.trim()) el.remove()
      })

    // Drop now-empty blocks and trailing line breaks left where the controls
    // were, otherwise their margins create a big gap before the pills.
    doc.body.querySelectorAll('p, pre, code, div, span, label').forEach((el) => {
      if (!el.textContent?.trim()) el.remove()
    })
    doc.body.querySelectorAll('br').forEach((br) => {
      let node = br.nextSibling
      while (node) {
        if (node.textContent?.trim()) return
        node = node.nextSibling
      }
      br.remove()
    })
    const html = doc.body.innerHTML.trim()

    return (
      <div className='[&_a]:text-theme-blue [&_a]:underline [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:mb-1'>
        {html && <div dangerouslySetInnerHTML={{ __html: html }} />}

        {showControls && instantOptions.length > 0 && (
          <div className='mt-2 flex flex-wrap gap-2'>
            {instantOptions.map((option, j) => (
              <button
                key={j}
                type='button'
                onClick={() => submitMessage(option)}
                className={`${pillClass} bg-white text-theme-blue hover:bg-theme-blue hover:text-white`}
              >
                {option}
              </button>
            ))}
          </div>
        )}

        {showControls && checkboxes.length > 0 && (
          <CheckboxOptions
            options={checkboxes}
            input={input}
            onToggle={toggleOption}
          />
        )}
      </div>
    )
  }

  const renderAssistantBody = (message: ChatMessage, index: number) => {
    // No content yet → "thinking" bubble.
    if (!message.content) {
      return <ThinkingDots />
    }

    // Still streaming → readable text only (markup hidden). Once the options
    // markup starts arriving, swap the cursor for a "building options" hint so
    // the user knows something is still happening.
    if (index === streamingIndex) {
      const buildingOptions = CONTROL_MARKUP.test(message.content)
      return (
        <div className='whitespace-pre-line'>
          {streamingDisplay(message.content)}
          {buildingOptions
            ? (
              <div className='mt-2 flex items-center gap-2 text-gray-500'>
                <ThinkingDots />
                <span className='text-sm'>
                  {t('budget:aiGuidePaywall.buildingOptions')}
                </span>
              </div>
            )
            : (
              <span className='ml-0.5 inline-block w-1.5 animate-pulse'>▍</span>
            )}
        </div>
      )
    }

    // Finished → rendered Markdown. Only the last message keeps its answer
    // controls; once the user replies, earlier questions' controls are removed.
    return renderAssistantMarkdown(message.content, index === messages.length - 1)
  }

  // Which of the completion overlays is visible: the payment confirmation, or
  // the spinner (shown while the budget JSON streams in and while it's built).
  const showConfirm =
    isCompleting && pendingCompletion !== null && !isCreating && !isReady
  const showSpinner = isCompleting && !isReady && !showConfirm

  return (
    <div className='relative flex flex-col h-[70vh]'>
      {!isCompleting && (
        <Headline className='mb-5'>{t('budget:aiGuidePaywall.title')}</Headline>
      )}

      <div
        className={`flex min-h-0 flex-1 flex-col transition-opacity duration-500 ${
          isCompleting ? 'pointer-events-none opacity-0' : 'opacity-100'
        }`}
      >
        <div ref={scrollRef} className='flex-1 overflow-y-auto pr-2 space-y-4'>
          {messages.map((message, index) =>
            message.role === 'assistant'
              ? (
                <div key={index} className='flex items-end gap-2'>
                  <FaPiggyBank
                    size={40}
                    className='flex-shrink-0 text-theme-blue'
                    title={t('budget:aiGuidePaywall.assistantName')}
                  />
                  <div className='max-w-[75%] rounded-2xl rounded-bl-sm border border-theme-blue bg-white px-4 py-3 text-gray-900'>
                    {renderAssistantBody(message, index)}
                  </div>
                </div>
              )
              : (
                <div key={index} className='flex items-end justify-end gap-2'>
                  <div className='max-w-[75%] whitespace-pre-line rounded-2xl rounded-br-sm bg-theme-blue px-4 py-3 text-white'>
                    {message.content}
                  </div>
                  <div className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-theme-dark-blue text-white'>
                    <MdPerson size='1.5rem' />
                  </div>
                </div>
              ),
          )}
        </div>

        <form
          onSubmit={sendMessage}
          className='mt-4 flex items-center gap-2 border-t pt-4'
        >
          <input
            ref={inputRef}
            type='text'
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('budget:aiGuidePaywall.inputPlaceholder')}
            className='flex-1 rounded-full border border-gray-300 px-4 py-2 text-gray-900 focus:border-theme-blue focus:outline-none'
          />
          <button
            type='submit'
            disabled={!input.trim() || isStreaming}
            className='flex-shrink-0 rounded-full bg-theme-blue px-5 py-2 text-sm font-semibold text-white transition hover:bg-theme-dark-blue disabled:cursor-not-allowed disabled:opacity-50'
          >
            {t('budget:aiGuidePaywall.sendButton')}
          </button>
        </form>
      </div>

      {isCompleting && (
        <div className='absolute inset-0'>
          {/* Building state — spinner while the budget streams in / is created. */}
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center transition-opacity duration-500 ${
              showSpinner ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <LoadingSpinner className='text-theme-blue' />
            <div>
              <Text className='font-semibold'>
                {t('budget:aiGuidePaywall.readyText')}
              </Text>
              <Text
                className={`transition-opacity duration-500 ${
                  lineVisible ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {LOADING_MESSAGES[loadingStep]}
              </Text>
            </div>
          </div>

          {/* Payment confirmation — shown once the budget is ready to create. */}
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center gap-4 overflow-y-auto px-6 transition-opacity duration-500 ${
              showConfirm ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <div className='max-w-md'>
              <div className='mb-4 flex items-center gap-2'>
                <MdCheckCircle
                  size={28}
                  className='flex-shrink-0 text-green-500'
                />
                <Headline size='lg'>
                  {t('budget:aiGuidePaywall.budgetReadyTitle')}
                </Headline>
              </div>
              <Text className='mb-4'>
                {t('budget:aiGuidePaywall.confirmText')}
              </Text>
              {!hasPhone && (
                <div className='mb-4 w-full'>
                  <label className='mb-1 block text-sm text-gray-900'>
                    {t('budget:aiGuidePaywall.phoneLabel')}
                  </label>
                  <input
                    type='tel'
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={t('budget:aiGuidePaywall.phoneLabel')}
                    required
                    className='w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-theme-blue focus:outline-none'
                  />
                </div>
              )}
              <label className='mb-6 flex cursor-pointer items-start gap-2'>
                <input
                  type='checkbox'
                  checked={conditionsAccepted}
                  onChange={(e) => setConditionsAccepted(e.target.checked)}
                  className='mt-1'
                />
                <span className='text-sm text-gray-900'>
                  {t('budget:aiGuidePaywall.acceptTerms')}{' '}
                  <a
                    href='https://xn--privat-konomi-hnb.dk/handelsbetingelser/'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-theme-blue underline'
                  >
                    {t('budget:aiGuidePaywall.termsLink')}
                  </a>
                </span>
              </label>
              <Button
                onClick={confirmAndCreate}
                loading={isCreating}
                disabled={!conditionsAccepted || (!hasPhone && !phone.trim())}
              >
                {t('budget:aiGuidePaywall.confirmButton')}
              </Button>
            </div>
          </div>

          {/* Ready state — checkmark + confirmation fade in. */}
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center transition-opacity duration-500 ${
              isReady ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <MdCheckCircle size={40} className='text-green-500' />
            <Text className='font-semibold'>
              {t('budget:aiGuidePaywall.readyDone')}
            </Text>
          </div>
        </div>
      )}
    </div>
  )
}

export default AIGuidePaywall
